import React, { useState, useRef, useEffect } from 'react';
import { aiAPI } from '../services/aiBackend';

const AIWidget = ({ platform, data, onClose, isConfigured = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [configStatus, setConfigStatus] = useState({});
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Check configuration status
    checkConfigStatus();
    // Generate contextual suggestions based on platform and data
    generateContextualSuggestions();
  }, [platform, data]);

  const checkConfigStatus = async () => {
    try {
      const response = await fetch('https://cgb-backend.carl-g-bisaillon.workers.dev/api/config/status');
      if (response.ok) {
        const data = await response.json();
        setConfigStatus(data.config || {});
      }
    } catch (error) {
      console.error('Failed to fetch configuration status:', error);
    }
  };

  const generateContextualSuggestions = () => {
    const platformSuggestions = {
      facebook: [
        "Generate a Facebook post about tonight's show",
        "Create engaging content for Facebook",
        "Suggest Facebook hashtags",
        "Analyze Facebook engagement trends"
      ],
      instagram: [
        "Create an Instagram story idea",
        "Generate Instagram post captions",
        "Suggest Instagram hashtags",
        "Create Instagram reel concepts"
      ],
      tiktok: [
        "Create a TikTok video idea",
        "Generate TikTok captions",
        "Suggest trending TikTok hashtags",
        "Create TikTok challenge ideas"
      ],
      youtube: [
        "Create YouTube Shorts content",
        "Generate YouTube video titles",
        "Suggest YouTube tags",
        "Create YouTube thumbnail ideas"
      ],
      google: [
        "Analyze website traffic patterns",
        "Suggest SEO improvements",
        "Generate content for website",
        "Analyze user behavior"
      ],
      cloudflare: [
        "Analyze website performance",
        "Suggest security improvements",
        "Generate performance report",
        "Analyze traffic patterns"
      ]
    };

    setSuggestions(platformSuggestions[platform] || [
      "Generate content for this platform",
      "Analyze performance metrics",
      "Suggest improvements",
      "Create engaging posts"
    ]);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Create context-aware prompt
      const contextPrompt = `You are an AI assistant for La Soirée du Rire comedy show. 
      
Platform: ${platform}
Current Data: ${JSON.stringify(data, null, 2)}

User Request: ${inputMessage}

Please provide a helpful response that considers the platform context and current data.`;

      const aiResponse = await aiAPI.generateContent({
        prompt: contextPrompt,
        platform: platform,
        contentType: 'post',
        maxLength: 500,
        includeHashtags: true,
        includeEmojis: true,
        userId: 'dashboard-user'
      });

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: aiResponse.generatedContent || aiResponse.content || aiResponse.message || 'I apologize, but I couldn\'t generate a response at the moment. Please try again.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI Widget Error:', error);
      
      // Provide contextual fallback response
      const fallbackResponse = getContextualFallback(inputMessage, platform, data);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: fallbackResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const getContextualFallback = (message, platform, data) => {
    const lowerMessage = message.toLowerCase();
    const isPlatformConfigured = configStatus[platform]?.configured || false;
    
    if (lowerMessage.includes('generate') || lowerMessage.includes('create')) {
      return `Here's a great content idea for ${platform}:

🎭 **"Get ready for an unforgettable night of laughter at La Soirée du Rire!"**

📊 **Current Performance:**
• ${data?.followers || 'N/A'} followers
• ${data?.engagement || 'N/A'}% engagement rate
• ${data?.trend || 'N/A'} growth trend

💡 **Content Suggestions:**
• Behind-the-scenes content
• Comedian spotlights
• Audience reactions
• Show highlights

${isPlatformConfigured ? '✨ **AI-powered content generation is ready!** Try asking for specific content types.' : '🔧 To enable AI content generation, please configure the API keys in your deployment.'}`;
    }
    
    if (lowerMessage.includes('analyze') || lowerMessage.includes('insight')) {
      return `Here's an analysis of your ${platform} performance:

📈 **Key Metrics:**
• Followers: ${data?.followers || 'N/A'}
• Engagement: ${data?.engagement || 'N/A'}%
• Reach: ${data?.reach || 'N/A'}
• Trend: ${data?.trend || 'N/A'}

🎯 **Recommendations:**
• Post more frequently during peak hours
• Use trending hashtags
• Engage with audience comments
• Create interactive content

${isPlatformConfigured ? '✨ **AI-powered insights are available!** Ask for specific analytics or trends.' : '🔧 To get AI-powered insights, please configure the API keys.'}`;
    }
    
    return `I can help you with ${platform} strategy! Here are some suggestions:

🎭 **Content Ideas:**
• Show announcements
• Comedian features
• Audience testimonials
• Behind-the-scenes content

📊 **Analytics Tips:**
• Track engagement rates
• Monitor posting times
• Analyze audience demographics
• Measure content performance

${isPlatformConfigured ? '✨ **Full AI capabilities are ready!** Ask me anything about your ${platform} strategy.' : '🔧 To unlock full AI capabilities, please configure the API keys in your deployment.'}`;
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    setIsExpanded(true);
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`ai-widget ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="ai-widget-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="ai-widget-title">
          <div className="ai-avatar">🤖</div>
          <span>AI Assistant</span>
        </div>
        <div className="ai-widget-actions">
          <button className="ai-widget-toggle">
            {isExpanded ? '−' : '+'}
          </button>
          {onClose && (
            <button className="ai-widget-close" onClick={onClose}>
              ✕
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="ai-widget-content">
          <div className="ai-widget-suggestions">
            <h4>Quick Actions</h4>
            <div className="suggestion-chips">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="suggestion-chip"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {messages.length > 0 && (
            <div className="ai-widget-messages">
              {messages.map((message) => (
                <div key={message.id} className={`ai-widget-message ${message.type}`}>
                  <div className="ai-widget-message-content">
                    {message.content}
                  </div>
                  <div className="ai-widget-message-time">
                    {formatTime(message.timestamp)}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="ai-widget-message ai">
                  <div className="ai-widget-message-content">
                    <div className="ai-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          <form className="ai-widget-input" onSubmit={handleSendMessage}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={`Ask about ${platform} strategy...`}
              disabled={isLoading}
            />
            <button type="submit" disabled={!inputMessage.trim() || isLoading}>
              {isLoading ? '⏳' : '➤'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AIWidget;
